import * as React from 'react';
import cookie from 'js-cookie';

import type {
  ChooseRequestContext,
  Choice,
  Cookie,
  ChooseRequestOptions,
  DyTemplates,
  CustomJsonVariation,
  DyMultiVariationTemplates,
} from './types';
import { DyClient } from './createClient';
import { getMultiVariationElements, getVariationElements } from './utils';

interface DYContextValue {
  choices: Choice[];
  client: DyClient;
  templates: DyTemplates;
  multiVariationTemplates: DyMultiVariationTemplates;
  context?: ChooseRequestContext;
}

export const DyContext = React.createContext({} as DYContextValue);

interface DyProviderProps {
  cookies?: Cookie[];
  choices?: Choice[];
  context?: ChooseRequestContext;
}

interface ProviderProps extends DyProviderProps {
  client: DyClient;
  templates: DyTemplates;
  multiVariationTemplates: DyMultiVariationTemplates;
}

const Provider: React.FC<ProviderProps> = ({
  children,
  client,
  context,
  multiVariationTemplates = {},
  templates = {},
  cookies = [],
  choices = [],
}) => {
  React.useEffect(() => {
    cookies.forEach(({ name, value, maxAge }) => {
      const expires = new Date(Date.now() + Number(maxAge) * 1000);
      cookie.set(name, value, { expires });
    });
  }, [cookies]);

  return (
    <DyContext.Provider value={{ templates, choices, client, context, multiVariationTemplates }}>
      {children}
    </DyContext.Provider>
  );
};

interface CreateDyProviderParams {
  templates: DyTemplates;
  multiVariationTemplates: DyMultiVariationTemplates;
  client: DyClient;
}

export function createDyProvider(params: CreateDyProviderParams): React.FC<DyProviderProps> {
  const { multiVariationTemplates, templates, client } = params;
  const DyProvider: React.FC<DyProviderProps> = ({ children, choices, cookies, context }) => (
    <Provider
      client={client}
      multiVariationTemplates={multiVariationTemplates}
      templates={templates}
      choices={choices}
      cookies={cookies}
      context={context}
    >
      {children}
    </Provider>
  );
  DyProvider.displayName = 'DyProvider';

  return DyProvider;
}

interface DyContextContextValue {
  context: ChooseRequestContext;
}

const DyContextContext = React.createContext({} as DyContextContextValue);

interface DyContextProps {
  context?: ChooseRequestContext;
}

export const DyContextProvider: React.FC<DyContextProps> = ({ context, children }) => {
  return <DyContextContext.Provider value={{ context }}>{children}</DyContextContext.Provider>;
};

interface DyCampaignProps {
  name: string;
  options?: ChooseRequestOptions;
}

export const DyCampaign: React.FC<DyCampaignProps> = ({ name, options }) => {
  const { context: defaultContext } = React.useContext(DyContextContext);
  const {
    client,
    templates,
    multiVariationTemplates,
    choices = [],
    context = defaultContext,
  } = React.useContext(DyContext);
  if (!client) {
    throw new Error('`DyProvider` not found, please make sure you call this hook inside of a `DyProvider`');
  }
  const isLoadedRef = React.useRef(false);
  const [choice, setChoice] = React.useState(choices.find((choice) => choice.name === name));

  const loadChoice = React.useCallback(() => {
    client
      .choose({ body: { selector: { names: [name] }, context, options } })
      .then(({ choices }) => {
        setChoice(choices.find((choice) => choice.name === name));
      })
      .catch((error) => console.log(error));
  }, [client, context, name, options]);

  React.useEffect(() => {
    const choice = choices.find((choice) => choice.name === name);
    if (choice) {
      setChoice(choice);
    }
  }, [choices, name]);

  React.useEffect(() => {
    if (!isLoadedRef.current && !choice && client.isEnabled) {
      isLoadedRef.current = true;
      loadChoice();
    }
  }, [choice, client.isEnabled, loadChoice, name]);

  const handleClick = React.useCallback(() => {
    if (choice && choice.decisionId) {
      client.engagements({
        engagements: [
          {
            decisionId: choice.decisionId,
            type: 'CLICK',
            // TODO: verify if we can just send variations like that
            // variations: choice.variations.map((variation) => variation.id),
          },
        ],
      });
    }
  }, [choice, client]);

  const variations = (choice ? choice?.variations : []) as CustomJsonVariation[];
  return (
    <>
      {getVariationElements(templates, handleClick, variations)}
      {getMultiVariationElements(multiVariationTemplates, handleClick, variations)}
    </>
  );
};

export function useDyClient() {
  const { client, context } = React.useContext(DyContext);
  return { client, context };
}
